import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, MapPin, Calendar, Clock, User, Phone, Mail, 
  CreditCard, ChevronRight, ChevronLeft, Ambulance, CircleUser, 
  Zap, Building2, Check, Loader2, AlertCircle, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { format } from 'date-fns';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons
const pickupIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const dropoffIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const SERVICES = [
  { id: 'ambulatory', title: 'Ambulatory', description: 'Walk with minimal assistance', icon: Ambulance },
  { id: 'wheelchair', title: 'Wheelchair', description: 'Manual wheelchair transport', icon: CircleUser },
  { id: 'electric_wheelchair', title: 'Electric Wheelchair', description: 'Power wheelchair equipped', icon: Zap },
  { id: 'hospital_discharge', title: 'Hospital Discharge', description: 'Post-hospital transport', icon: Building2 }
];

const TIME_SLOTS = [
  '06:00 AM', '06:30 AM', '07:00 AM', '07:30 AM', '08:00 AM', '08:30 AM',
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
  '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
  '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM', '08:00 PM', '08:30 PM',
  '09:00 PM', '09:30 PM', '10:00 PM'
];

// Convert a "06:30 AM" slot string into a Date on the given base date
const parseSlotToDate = (slot, baseDate) => {
  const [time, meridiem] = slot.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (meridiem === 'PM' && h !== 12) h += 12;
  if (meridiem === 'AM' && h === 12) h = 0;
  const d = new Date(baseDate);
  d.setHours(h, m, 0, 0);
  return d;
};

// Map component to fit bounds
function MapBounds({ pickup, dropoff }) {
  const map = useMap();
  
  useEffect(() => {
    if (pickup && dropoff) {
      const bounds = L.latLngBounds([pickup, dropoff]);
      map.fitBounds(bounds, { padding: [50, 50] });
    } else if (pickup) {
      map.setView(pickup, 13);
    } else if (dropoff) {
      map.setView(dropoff, 13);
    }
  }, [pickup, dropoff, map]);
  
  return null;
}

export default function BookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    pickup_address: '',
    pickup_lat: null,
    pickup_lng: null,
    dropoff_address: '',
    dropoff_lat: null,
    dropoff_lng: null,
    service_type: '',
    trip_type: 'one_way',
    pickup_date: null,
    pickup_time: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    number_of_patients: '1',
    patients: [{ name: '', age: '' }],
    special_instructions: '',
    payment_method: 'stripe'
  });
  
  const [distance, setDistance] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [routeCoords, setRouteCoords] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  const [searchingPickup, setSearchingPickup] = useState(false);
  const [searchingDropoff, setSearchingDropoff] = useState(false);
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  
  const searchTimeoutRef = useRef(null);

  // Admin-only "rides booked this day" viewer (privacy: details shown only when signed in as admin)
  const [isAdmin, setIsAdmin] = useState(false);
  const [dayBookings, setDayBookings] = useState([]);
  const [loadingDayBookings, setLoadingDayBookings] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleDateOpen, setRescheduleDateOpen] = useState(false);

  const statusBadge = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-600/20 text-green-400 border-green-500/30';
      case 'completed': return 'bg-blue-600/20 text-blue-400 border-blue-500/30';
      case 'cancelled': return 'bg-red-600/20 text-red-400 border-red-500/30';
      default: return 'bg-amber-600/20 text-amber-400 border-amber-500/30';
    }
  };

  // Load bookings for a selected day. Admin status is derived from whether the
  // authenticated /bookings call succeeds (privacy: details only for valid admins).
  // Called from event handlers only (never an effect) to avoid extra renders.
  const fetchDayBookings = useCallback(async (dateObj) => {
    const token = localStorage.getItem('adminToken');
    if (!token || !dateObj) { setIsAdmin(false); setDayBookings([]); return; }
    setLoadingDayBookings(true);
    try {
      const res = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setIsAdmin(true);
      const dayStr = format(dateObj, 'yyyy-MM-dd');
      const list = res.data
        .filter((b) => b.pickup_date === dayStr)
        .slice()
        .sort((a, b) => (a.pickup_time || '').localeCompare(b.pickup_time || ''));
      setDayBookings(list);
    } catch (e) {
      setIsAdmin(false);
      setDayBookings([]);
    } finally {
      setLoadingDayBookings(false);
    }
  }, []);

  const handlePickupDateSelect = (date) => {
    if (!date) return;
    setFormData((prev) => ({ ...prev, pickup_date: date, pickup_time: '' }));
    fetchDayBookings(date);
  };

  const cancelDayBooking = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(`${API}/bookings/${id}`, { booking_status: 'cancelled' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Booking cancelled');
      fetchDayBookings(formData.pickup_date);
    } catch (e) {
      toast.error('Failed to cancel booking');
    }
  };

  const openReschedule = (booking) => {
    setRescheduleBooking(booking);
    setRescheduleDate(booking.pickup_date ? new Date(booking.pickup_date + 'T00:00:00') : new Date());
    setRescheduleTime(booking.pickup_time || '');
    setShowReschedule(true);
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) { toast.error('Please select a date and time'); return; }
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(`${API}/bookings/${rescheduleBooking.id}`, {
        pickup_date: format(rescheduleDate, 'yyyy-MM-dd'),
        pickup_time: rescheduleTime
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Booking rescheduled');
      setShowReschedule(false);
      setRescheduleBooking(null);
      fetchDayBookings(formData.pickup_date);
    } catch (e) {
      toast.error('Failed to reschedule booking');
    }
  };

  // Check if cancelled from payment
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Payment was cancelled. Please try again.');
    }
  }, [searchParams]);

  // Geocode address using Nominatim
  const searchAddress = useCallback(async (query, type) => {
    if (query.length < 3) {
      if (type === 'pickup') setPickupSuggestions([]);
      else setDropoffSuggestions([]);
      return;
    }

    if (type === 'pickup') setSearchingPickup(true);
    else setSearchingDropoff(true);

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ', Washington, USA')}&limit=5`
      );
      
      if (type === 'pickup') setPickupSuggestions(response.data);
      else setDropoffSuggestions(response.data);
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      if (type === 'pickup') setSearchingPickup(false);
      else setSearchingDropoff(false);
    }
  }, []);

  // Debounced address search
  const handleAddressChange = (value, type) => {
    setFormData(prev => ({
      ...prev,
      [`${type}_address`]: value,
      [`${type}_lat`]: null,
      [`${type}_lng`]: null
    }));
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(value, type);
    }, 500);
  };

  // Select address from suggestions
  const selectAddress = (suggestion, type) => {
    setFormData(prev => ({
      ...prev,
      [`${type}_address`]: suggestion.display_name,
      [`${type}_lat`]: parseFloat(suggestion.lat),
      [`${type}_lng`]: parseFloat(suggestion.lon)
    }));
    
    if (type === 'pickup') setPickupSuggestions([]);
    else setDropoffSuggestions([]);
  };

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c * 1.3; // Multiply by 1.3 for road distance approximation
  }, []);

  // Get actual route from OSRM
  const getRoute = useCallback(async (pickupLat, pickupLng, dropoffLat, dropoffLng) => {
    try {
      const response = await axios.get(
        `https://router.project-osrm.org/route/v1/driving/${pickupLng},${pickupLat};${dropoffLng},${dropoffLat}?overview=full&geometries=geojson`
      );
      
      if (response.data.routes && response.data.routes.length > 0) {
        const route = response.data.routes[0];
        // Convert GeoJSON coordinates [lng, lat] to Leaflet format [lat, lng]
        const coords = route.geometry.coordinates.map(coord => [coord[1], coord[0]]);
        setRouteCoords(coords);
        
        // Use OSRM distance (in meters, convert to miles)
        const distanceInMiles = route.distance / 1609.34;
        return distanceInMiles;
      }
    } catch (error) {
      console.error('Error getting route:', error);
      // Fallback to straight line
      setRouteCoords([
        [pickupLat, pickupLng],
        [dropoffLat, dropoffLng]
      ]);
    }
    return null;
  }, []);

  // Get route and pricing when both locations are set
  useEffect(() => {
    const fetchRouteAndPricing = async () => {
      if (formData.pickup_lat && formData.pickup_lng && formData.dropoff_lat && formData.dropoff_lng) {
        setCalculating(true);
        
        try {
          // Get actual route from OSRM
          const routeDistance = await getRoute(
            formData.pickup_lat, formData.pickup_lng,
            formData.dropoff_lat, formData.dropoff_lng
          );
          
          // Use route distance if available, otherwise calculate
          const dist = routeDistance || calculateDistance(
            formData.pickup_lat, formData.pickup_lng,
            formData.dropoff_lat, formData.dropoff_lng
          );
          setDistance(Math.round(dist * 10) / 10);
          
          // Get pricing from API
          const response = await axios.get(`${API}/pricing/calculate`, {
            params: { distance_miles: dist, trip_type: formData.trip_type }
          });
          setPricing(response.data);
        } catch (error) {
          console.error('Error calculating route:', error);
          if (error.response?.data?.detail) {
            toast.error(error.response.data.detail);
          }
        } finally {
          setCalculating(false);
        }
      }
    };
    
    fetchRouteAndPricing();
  }, [formData.pickup_lat, formData.pickup_lng, formData.dropoff_lat, formData.dropoff_lng, formData.trip_type, calculateDistance, getRoute]);

  // Handle number of patients change
  const handlePatientCountChange = (value) => {
    const count = parseInt(value);
    const currentPatients = [...formData.patients];
    
    if (count > currentPatients.length) {
      // Add more patient slots
      while (currentPatients.length < count) {
        currentPatients.push({ name: '', age: '' });
      }
    } else {
      // Remove extra patient slots
      currentPatients.splice(count);
    }
    
    setFormData(prev => ({
      ...prev,
      number_of_patients: value,
      patients: currentPatients
    }));
  };

  // Update patient info
  const updatePatient = (index, field, value) => {
    const updatedPatients = [...formData.patients];
    updatedPatients[index] = { ...updatedPatients[index], [field]: value };
    setFormData(prev => ({ ...prev, patients: updatedPatients }));
  };

  // Validate current step
  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.pickup_lat || !formData.dropoff_lat) {
          toast.error('Please select both pickup and dropoff locations');
          return false;
        }
        if (distance > 500) {
          toast.error('Sorry, we only service up to 500 miles');
          return false;
        }
        return true;
      case 2:
        if (!formData.service_type) {
          toast.error('Please select a service type');
          return false;
        }
        return true;
      case 3:
        if (!formData.pickup_date || !formData.pickup_time) {
          toast.error('Please select pickup date and time');
          return false;
        }
        return true;
      case 4:
        if (!formData.customer_name || !formData.customer_phone || !formData.customer_email) {
          toast.error('Please fill in all required contact fields');
          return false;
        }
        // Validate all patients have name and age
        for (let i = 0; i < formData.patients.length; i++) {
          if (!formData.patients[i].name || !formData.patients[i].age) {
            toast.error(`Please fill in all patient information`);
            return false;
          }
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.customer_email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  // Create booking
  const createBooking = async () => {
    setLoading(true);
    
    try {
      // Combine patient info for backend
      const patientInfo = formData.patients.map((p, i) => `Patient ${i+1}: ${p.name} (Age: ${p.age})`).join('; ');
      const firstPatientAge = formData.patients[0]?.age || '';
      
      const bookingData = {
        ...formData,
        pickup_date: format(formData.pickup_date, 'yyyy-MM-dd'),
        distance_miles: distance,
        estimated_price: pricing.estimated_price,
        patient_age: firstPatientAge,
        special_instructions: formData.special_instructions + (formData.patients.length > 1 ? `\n\nAll Patients: ${patientInfo}` : '')
      };
      
      // Remove patients array before sending
      delete bookingData.patients;
      
      const response = await axios.post(`${API}/bookings`, bookingData);
      setBookingId(response.data.id);
      return response.data.id;
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle Stripe payment
  const handleStripePayment = async () => {
    setLoading(true);
    
    try {
      const id = await createBooking();
      
      const response = await axios.post(`${API}/payments/stripe/create-session`, {
        booking_id: id,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Stripe error:', error);
      toast.error('Payment initialization failed. Please try again.');
      setLoading(false);
    }
  };

  // Handle Pay Later booking
  const handlePayLater = async () => {
    setLoading(true);
    
    try {
      const id = await createBooking();
      toast.success('Booking created! We will contact you to confirm.');
      navigate(`/booking/success?booking_id=${id}&payment=pending`);
    } catch (error) {
      setLoading(false);
    }
  };

  // Handle PayPal payment
  const handlePayPalApprove = async (data) => {
    setLoading(true);
    
    try {
      await axios.post(`${API}/payments/paypal/capture/${data.orderID}`, null, {
        params: { booking_id: bookingId }
      });
      
      toast.success('Payment successful!');
      navigate(`/booking/success?booking_id=${bookingId}&payment=success`);
    } catch (error) {
      console.error('PayPal capture error:', error);
      toast.error('Payment capture failed. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const STEPS = [
    { num: 1, title: 'Location' },
    { num: 2, title: 'Service' },
    { num: 3, title: 'Schedule' },
    { num: 4, title: 'Details' },
    { num: 5, title: 'Payment' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-red-900/20">
      {/* Header */}
      <header className="bg-slate-900/95 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3 text-slate-300 hover:text-red-400 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_39e7a4cc-5fdf-434d-86d9-84f5c9734d0e/artifacts/1iyuca53_IMG_9172.png" 
                alt="MarcoPolo Logo" 
                className="w-8 h-8 object-contain rounded-full"
              />
              <span className="font-bold text-white">MarcoPolo</span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      currentStep > step.num 
                        ? 'bg-green-500 text-white' 
                        : currentStep === step.num 
                          ? 'bg-red-600 text-white shadow-lg shadow-red-900/50' 
                          : 'bg-slate-700 text-slate-400'
                    }`}
                  >
                    {currentStep > step.num ? <Check className="w-5 h-5" /> : step.num}
                  </div>
                  <span className={`mt-2 text-xs font-medium ${
                    currentStep >= step.num ? 'text-red-400' : 'text-slate-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-12 sm:w-24 h-1 mx-2 rounded ${
                    currentStep > step.num ? 'bg-green-500' : 'bg-slate-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-slate-800 rounded-2xl shadow-xl shadow-black/30 p-6 sm:p-8 border border-slate-700">
              {/* Step 1: Location */}
              {currentStep === 1 && (
                <div className="space-y-6 animate-fadeIn" data-testid="step-location">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Where are you going?</h2>
                    <p className="text-slate-400">Enter your pickup and drop-off locations</p>
                  </div>

                  {/* Pickup Address */}
                  <div className="space-y-2">
                    <Label htmlFor="pickup" className="flex items-center gap-2 text-slate-300">
                      <div className="w-6 h-6 bg-green-600/30 rounded-full flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-green-500" />
                      </div>
                      Pickup Location
                    </Label>
                    <div className="relative">
                      <Input
                        id="pickup"
                        data-testid="pickup-input"
                        placeholder="Enter pickup address..."
                        value={formData.pickup_address}
                        onChange={(e) => handleAddressChange(e.target.value, 'pickup')}
                        className="h-12 pl-4 pr-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500"
                      />
                      {searchingPickup && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500 animate-spin" />
                      )}
                      {formData.pickup_lat && (
                        <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                      )}
                    </div>
                    {pickupSuggestions.length > 0 && (
                      <div className="bg-slate-700 border border-slate-600 rounded-lg shadow-lg overflow-hidden">
                        {pickupSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            data-testid={`pickup-suggestion-${index}`}
                            className="w-full px-4 py-3 text-left hover:bg-slate-600 border-b border-slate-600 last:border-0 text-sm text-white"
                            onClick={() => selectAddress(suggestion, 'pickup')}
                          >
                            <p className="font-medium truncate">{suggestion.display_name}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Dropoff Address */}
                  <div className="space-y-2">
                    <Label htmlFor="dropoff" className="flex items-center gap-2 text-slate-300">
                      <div className="w-6 h-6 bg-red-600/30 rounded-full flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-red-500" />
                      </div>
                      Drop-off Location
                    </Label>
                    <div className="relative">
                      <Input
                        id="dropoff"
                        data-testid="dropoff-input"
                        placeholder="Enter drop-off address..."
                        value={formData.dropoff_address}
                        onChange={(e) => handleAddressChange(e.target.value, 'dropoff')}
                        className="h-12 pl-4 pr-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500"
                      />
                      {searchingDropoff && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500 animate-spin" />
                      )}
                      {formData.dropoff_lat && (
                        <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                      )}
                    </div>
                    {dropoffSuggestions.length > 0 && (
                      <div className="bg-slate-700 border border-slate-600 rounded-lg shadow-lg overflow-hidden">
                        {dropoffSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            data-testid={`dropoff-suggestion-${index}`}
                            className="w-full px-4 py-3 text-left hover:bg-slate-600 border-b border-slate-600 last:border-0 text-sm text-white"
                            onClick={() => selectAddress(suggestion, 'dropoff')}
                          >
                            <p className="font-medium truncate">{suggestion.display_name}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Trip Type */}
                  <div className="space-y-3">
                    <Label className="text-slate-300">Trip Type</Label>
                    <RadioGroup
                      value={formData.trip_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, trip_type: value }))}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="one_way" id="one_way" data-testid="trip-one-way" className="border-slate-500 text-red-500" />
                        <Label htmlFor="one_way" className="cursor-pointer text-white">One Way</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="round_trip" id="round_trip" data-testid="trip-round-trip" className="border-slate-500 text-red-500" />
                        <Label htmlFor="round_trip" className="cursor-pointer text-white">Round Trip</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Map */}
                  <div className="h-[400px] rounded-xl overflow-hidden border border-slate-600 shadow-inner">
                    <MapContainer
                      center={[47.6062, -122.3321]} // Seattle, WA
                      zoom={8}
                      className="h-full w-full"
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {formData.pickup_lat && (
                        <Marker position={[formData.pickup_lat, formData.pickup_lng]} icon={pickupIcon} />
                      )}
                      {formData.dropoff_lat && (
                        <Marker position={[formData.dropoff_lat, formData.dropoff_lng]} icon={dropoffIcon} />
                      )}
                      {routeCoords.length > 1 && (
                        <Polyline positions={routeCoords} color="#DC2626" weight={4} />
                      )}
                      <MapBounds 
                        pickup={formData.pickup_lat ? [formData.pickup_lat, formData.pickup_lng] : null}
                        dropoff={formData.dropoff_lat ? [formData.dropoff_lat, formData.dropoff_lng] : null}
                      />
                    </MapContainer>
                  </div>
                </div>
              )}

              {/* Step 2: Service Type */}
              {currentStep === 2 && (
                <div className="space-y-6 animate-fadeIn" data-testid="step-service">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Select Service Type</h2>
                    <p className="text-slate-400">Choose the type of transport you need</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {SERVICES.map((service) => (
                      <button
                        key={service.id}
                        data-testid={`service-${service.id}`}
                        onClick={() => setFormData(prev => ({ ...prev, service_type: service.id }))}
                        className={`p-6 rounded-xl border-2 text-left transition-all ${
                          formData.service_type === service.id
                            ? 'border-red-500 bg-red-600/20 shadow-lg shadow-red-900/30'
                            : 'border-slate-600 hover:border-red-400 bg-slate-700/50'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                          formData.service_type === service.id ? 'bg-red-600' : 'bg-slate-600'
                        }`}>
                          <service.icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-1">{service.title}</h3>
                        <p className="text-sm text-slate-400">{service.description}</p>
                        {formData.service_type === service.id && (
                          <div className="mt-4 flex items-center gap-2 text-red-400">
                            <Check className="w-5 h-5" />
                            <span className="text-sm font-medium">Selected</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Schedule */}
              {currentStep === 3 && (
                <div className="space-y-6 animate-fadeIn" data-testid="step-schedule">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Schedule Your Ride</h2>
                    <p className="text-slate-400">Select your preferred date and time</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    {/* Date Picker */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-slate-300">
                        <Calendar className="w-4 h-4" />
                        Pickup Date
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid="date-picker-btn"
                            className={`w-full h-12 justify-start text-left font-normal bg-slate-700 border-slate-600 text-white hover:bg-slate-600 ${
                              !formData.pickup_date && 'text-slate-400'
                            }`}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {formData.pickup_date ? format(formData.pickup_date, 'PPP') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-600" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={formData.pickup_date}
                            onSelect={handlePickupDateSelect}
                            disabled={(date) => {
                              const startToday = new Date();
                              startToday.setHours(0, 0, 0, 0);
                              return date < startToday;
                            }}
                            initialFocus
                            className="bg-slate-800 text-white"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Time Picker */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-slate-300">
                        <Clock className="w-4 h-4" />
                        Pickup Time
                      </Label>
                      <Select
                        value={formData.pickup_time}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, pickup_time: value }))}
                      >
                        <SelectTrigger data-testid="time-picker" className="h-12 bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600 max-h-64">
                          {(() => {
                            const slots = TIME_SLOTS.filter((time) => {
                              if (!formData.pickup_date) return true;
                              const sel = new Date(formData.pickup_date);
                              const now = new Date();
                              if (sel.toDateString() !== now.toDateString()) return true;
                              return parseSlotToDate(time, sel) > now;
                            });
                            if (slots.length === 0) {
                              return <div className="px-3 py-2 text-sm text-slate-400">No more slots available today — please select a future date</div>;
                            }
                            return slots.map((time) => (
                              <SelectItem key={time} value={time} className="text-white hover:bg-slate-600">{time}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Selected DateTime Summary */}
                  {formData.pickup_date && formData.pickup_time && (
                    <div className="p-4 bg-red-600/20 rounded-xl border border-red-500/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-400">Scheduled Pickup</p>
                          <p className="font-bold text-white">
                            {format(formData.pickup_date, 'EEEE, MMMM d, yyyy')} at {formData.pickup_time}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Admin-only: rides already booked on the selected day */}
                  {isAdmin && formData.pickup_date && (
                    <div className="p-4 bg-slate-800/60 rounded-xl border border-slate-700" data-testid="day-bookings-panel">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-white flex items-center gap-2">
                          <Users className="w-4 h-4 text-red-400" />
                          Rides booked on {format(formData.pickup_date, 'MMM d, yyyy')}
                        </h3>
                        <span className="text-xs text-slate-400" data-testid="day-bookings-count">
                          {dayBookings.length} ride{dayBookings.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {loadingDayBookings ? (
                        <p className="text-sm text-slate-400">Loading…</p>
                      ) : dayBookings.length === 0 ? (
                        <p className="text-sm text-slate-400">No rides booked on this day yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {dayBookings.map((b) => (
                            <div key={b.id} className="p-3 bg-slate-700/40 rounded-lg border border-slate-600" data-testid={`day-booking-${b.id}`}>
                              <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="space-y-1 text-sm">
                                  <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
                                    <Clock className="w-3.5 h-3.5 text-red-400" /> {b.pickup_time}
                                    <span className="text-slate-400 font-normal">· {b.customer_name}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize border ${statusBadge(b.booking_status)}`}>{b.booking_status}</span>
                                  </p>
                                  <p className="flex items-center gap-2 text-slate-300"><Phone className="w-3.5 h-3.5 text-slate-400" /> {b.customer_phone}</p>
                                  <p className="flex items-center gap-2 text-slate-300"><Mail className="w-3.5 h-3.5 text-slate-400" /> {b.customer_email}</p>
                                  <p className="flex items-start gap-2 text-slate-400"><MapPin className="w-3.5 h-3.5 text-green-500 mt-0.5" /> {b.pickup_address}</p>
                                  <p className="flex items-start gap-2 text-slate-400"><MapPin className="w-3.5 h-3.5 text-red-500 mt-0.5" /> {b.dropoff_address}</p>
                                  <p className="text-emerald-400 font-bold">${b.estimated_price?.toFixed(2)}</p>
                                </div>
                                <div className="flex flex-col gap-2">
                                  <Button type="button" size="sm" variant="outline" onClick={() => openReschedule(b)} data-testid={`day-reschedule-${b.id}`} className="gap-2 bg-transparent border-slate-600 text-white hover:bg-slate-600 h-8 text-xs">
                                    <Calendar className="w-3.5 h-3.5" /> Edit
                                  </Button>
                                  <Button type="button" size="sm" variant="outline" disabled={b.booking_status === 'cancelled'} onClick={() => cancelDayBooking(b.id)} data-testid={`day-cancel-${b.id}`} className="gap-2 bg-transparent border-red-500/40 text-red-400 hover:bg-red-600/20 h-8 text-xs disabled:opacity-40">
                                    <AlertCircle className="w-3.5 h-3.5" /> Cancel
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Contact Details */}
              {currentStep === 4 && (
                <div className="space-y-6 animate-fadeIn" data-testid="step-details">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Patient & Contact Information</h2>
                    <p className="text-slate-400">Please provide patient and contact details</p>
                  </div>

                  <div className="space-y-4">
                    {/* Contact Person Info */}
                    <div className="p-4 bg-slate-700/50 rounded-xl border border-slate-600">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <User className="w-5 h-5 text-red-500" />
                        Contact Person
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
                          <Input
                            id="name"
                            data-testid="customer-name"
                            placeholder="Enter your full name"
                            value={formData.customer_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                            className="h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500"
                          />
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="phone" className="text-slate-300">Phone Number *</Label>
                            <Input
                              id="phone"
                              data-testid="customer-phone"
                              type="tel"
                              placeholder="(xxx) xxx-xxxx"
                              value={formData.customer_phone}
                              onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                              className="h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
                            <Input
                              id="email"
                              data-testid="customer-email"
                              type="email"
                              placeholder="your@email.com"
                              value={formData.customer_email}
                              onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                              className="h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Number of Patients */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-slate-300">
                        <Users className="w-4 h-4 text-red-500" />
                        Number of Patients *
                      </Label>
                      <Select
                        value={formData.number_of_patients}
                        onValueChange={handlePatientCountChange}
                      >
                        <SelectTrigger data-testid="num-patients" className="h-12 bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select number" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          <SelectItem value="1" className="text-white hover:bg-slate-600">1</SelectItem>
                          <SelectItem value="2" className="text-white hover:bg-slate-600">2</SelectItem>
                          <SelectItem value="3" className="text-white hover:bg-slate-600">3</SelectItem>
                          <SelectItem value="4" className="text-white hover:bg-slate-600">4</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Dynamic Patient Boxes */}
                    {formData.patients.map((patient, index) => (
                      <div key={index} className="p-4 bg-slate-700/50 rounded-xl border border-slate-600">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                          <User className="w-5 h-5 text-red-500" />
                          Patient {index + 1}
                        </h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-slate-300">Patient Name *</Label>
                            <Input
                              data-testid={`patient-name-${index}`}
                              placeholder="Enter patient name"
                              value={patient.name}
                              onChange={(e) => updatePatient(index, 'name', e.target.value)}
                              className="h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-300">Patient Age *</Label>
                            <Input
                              data-testid={`patient-age-${index}`}
                              type="number"
                              placeholder="Enter age"
                              min="0"
                              max="120"
                              value={patient.age}
                              onChange={(e) => updatePatient(index, 'age', e.target.value)}
                              className="h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="space-y-2">
                      <Label htmlFor="instructions" className="text-slate-300">
                        Special Instructions (Optional)
                      </Label>
                      <Textarea
                        id="instructions"
                        data-testid="special-instructions"
                        placeholder="Any special requirements or notes..."
                        value={formData.special_instructions}
                        onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 focus:border-red-500 min-h-[100px]"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Payment */}
              {currentStep === 5 && (
                <div className="space-y-6 animate-fadeIn" data-testid="step-payment">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Payment Method</h2>
                    <p className="text-slate-400">Choose how you&apos;d like to pay</p>
                  </div>

                  <div className="space-y-4">
                    {/* Payment Options */}
                    <RadioGroup
                      value={formData.payment_method}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))}
                      className="space-y-3"
                    >
                      <div 
                        className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                          formData.payment_method === 'stripe' 
                            ? 'border-red-500 bg-red-600/20' 
                            : 'border-slate-600 hover:border-red-400 bg-slate-700/50'
                        }`}
                        onClick={() => setFormData(prev => ({ ...prev, payment_method: 'stripe' }))}
                      >
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="stripe" id="stripe" data-testid="payment-stripe" className="border-slate-500 text-red-500" />
                          <Label htmlFor="stripe" className="flex items-center gap-3 cursor-pointer flex-1">
                            <CreditCard className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="font-semibold text-white">Credit/Debit Card</p>
                              <p className="text-sm text-slate-400">Secure payment via Stripe</p>
                            </div>
                          </Label>
                        </div>
                      </div>

                      <div 
                        className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                          formData.payment_method === 'paypal' 
                            ? 'border-red-500 bg-red-600/20' 
                            : 'border-slate-600 hover:border-red-400 bg-slate-700/50'
                        }`}
                        onClick={() => setFormData(prev => ({ ...prev, payment_method: 'paypal' }))}
                      >
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="paypal" id="paypal" data-testid="payment-paypal" className="border-slate-500 text-red-500" />
                          <Label htmlFor="paypal" className="flex items-center gap-3 cursor-pointer flex-1">
                            <div className="w-5 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">P</div>
                            <div>
                              <p className="font-semibold text-white">PayPal</p>
                              <p className="text-sm text-slate-400">Pay with your PayPal account</p>
                            </div>
                          </Label>
                        </div>
                      </div>

                      <div 
                        className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                          formData.payment_method === 'pay_later' 
                            ? 'border-red-500 bg-red-600/20' 
                            : 'border-slate-600 hover:border-red-400 bg-slate-700/50'
                        }`}
                        onClick={() => setFormData(prev => ({ ...prev, payment_method: 'pay_later' }))}
                      >
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="pay_later" id="pay_later" data-testid="payment-later" className="border-slate-500 text-red-500" />
                          <Label htmlFor="pay_later" className="flex items-center gap-3 cursor-pointer flex-1">
                            <Clock className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="font-semibold text-white">Pay Later / Cash</p>
                              <p className="text-sm text-slate-400">We&apos;ll contact you to arrange payment</p>
                            </div>
                          </Label>
                        </div>
                      </div>
                    </RadioGroup>

                    {/* Payment Action */}
                    <div className="pt-4">
                      {formData.payment_method === 'stripe' && (
                        <Button
                          data-testid="pay-stripe-btn"
                          onClick={handleStripePayment}
                          disabled={loading}
                          className="w-full bg-red-600 hover:bg-red-700 text-white h-14 rounded-xl text-lg font-semibold shadow-lg shadow-red-900/30"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5 mr-2" />
                              Pay ${pricing?.estimated_price?.toFixed(2)} with Card
                            </>
                          )}
                        </Button>
                      )}

                      {formData.payment_method === 'paypal' && (
                        <PayPalScriptProvider options={{ clientId: "test" }}>
                          <PayPalButtons
                            data-testid="paypal-buttons"
                            createOrder={async () => {
                              const id = await createBooking();
                              const response = await axios.post(`${API}/payments/paypal/create-order`, {
                                booking_id: id,
                                origin_url: window.location.origin
                              });
                              return response.data.order_id;
                            }}
                            onApprove={handlePayPalApprove}
                            onError={(err) => {
                              console.error('PayPal error:', err);
                              toast.error('PayPal payment failed. Please try again.');
                            }}
                            style={{ layout: 'vertical', color: 'blue', shape: 'rect' }}
                          />
                        </PayPalScriptProvider>
                      )}

                      {formData.payment_method === 'pay_later' && (
                        <Button
                          data-testid="book-later-btn"
                          onClick={handlePayLater}
                          disabled={loading}
                          className="w-full bg-slate-700 hover:bg-slate-600 text-white h-14 rounded-xl text-lg font-semibold"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                              Creating Booking...
                            </>
                          ) : (
                            <>
                              <Check className="w-5 h-5 mr-2" />
                              Confirm Booking (Pay Later)
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    <p className="text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Final price may vary based on specific requirements
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {currentStep < 5 && (
                <div className="flex justify-between mt-8 pt-6 border-t border-slate-700">
                  <Button
                    variant="outline"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    data-testid="prev-step-btn"
                    className="h-12 px-6 bg-transparent border-slate-600 text-white hover:bg-slate-700"
                  >
                    <ChevronLeft className="w-5 h-5 mr-2" />
                    Previous
                  </Button>
                  <Button
                    onClick={nextStep}
                    data-testid="next-step-btn"
                    className="h-12 px-8 bg-red-600 hover:bg-red-700 text-white"
                  >
                    Continue
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-slate-800 rounded-2xl shadow-xl shadow-black/30 p-6 sticky top-24 border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-6">Booking Summary</h3>
              
              <div className="space-y-4">
                {/* Route Summary */}
                {formData.pickup_address && (
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-400">From</p>
                        <p className="font-medium text-white truncate">{formData.pickup_address}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {formData.dropoff_address && (
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-400">To</p>
                        <p className="font-medium text-white truncate">{formData.dropoff_address}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Distance */}
                {distance && (
                  <div className="flex justify-between py-3 border-t border-slate-700">
                    <span className="text-slate-400">Distance</span>
                    <span className="font-semibold text-white">{distance} miles</span>
                  </div>
                )}

                {/* Trip Type */}
                <div className="flex justify-between py-3 border-t border-slate-700">
                  <span className="text-slate-400">Trip Type</span>
                  <span className="font-semibold text-white capitalize">
                    {formData.trip_type.replace('_', ' ')}
                  </span>
                </div>

                {/* Service */}
                {formData.service_type && (
                  <div className="flex justify-between py-3 border-t border-slate-700">
                    <span className="text-slate-400">Service</span>
                    <span className="font-semibold text-white capitalize">
                      {formData.service_type.replace('_', ' ')}
                    </span>
                  </div>
                )}

                {/* Number of Patients */}
                <div className="flex justify-between py-3 border-t border-slate-700">
                  <span className="text-slate-400">Patients</span>
                  <span className="font-semibold text-white">{formData.number_of_patients}</span>
                </div>

                {/* Schedule */}
                {formData.pickup_date && formData.pickup_time && (
                  <div className="py-3 border-t border-slate-700">
                    <p className="text-slate-400 text-sm mb-1">Scheduled</p>
                    <p className="font-semibold text-white">
                      {format(formData.pickup_date, 'MMM d, yyyy')}
                    </p>
                    <p className="text-red-400 font-medium">{formData.pickup_time}</p>
                  </div>
                )}

                {/* Price */}
                {pricing && (
                  <div className="mt-6 p-4 bg-red-600/20 rounded-xl border border-red-500/30">
                    {calculating ? (
                      <div className="flex items-center justify-center gap-2 py-2">
                        <Loader2 className="w-5 h-5 text-red-500 animate-spin" />
                        <span className="text-slate-300">Calculating...</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-300">Estimated Price</span>
                          <span className="text-2xl font-bold text-red-500">
                            ${pricing.estimated_price?.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Range: ${pricing.min_price?.toFixed(2)} - ${pricing.max_price?.toFixed(2)}
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Reschedule Dialog (admin only) */}
      <Dialog open={showReschedule} onOpenChange={setShowReschedule}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md" data-testid="booking-reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Booking</DialogTitle>
            <DialogDescription className="text-slate-400">Change the pickup date and time for this ride.</DialogDescription>
          </DialogHeader>
          {rescheduleBooking && (
            <div className="space-y-5">
              <div className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                <p className="font-semibold text-white">{rescheduleBooking.customer_name}</p>
                <p className="text-sm text-slate-400">
                  Currently: {rescheduleBooking.pickup_date} at {rescheduleBooking.pickup_time}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">New Date</Label>
                <Popover open={rescheduleDateOpen} onOpenChange={setRescheduleDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="booking-reschedule-date-btn"
                      className={`w-full h-11 justify-start text-left font-normal bg-slate-700 border-slate-600 text-white hover:bg-slate-600 ${!rescheduleDate && 'text-slate-400'}`}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {rescheduleDate ? format(rescheduleDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-600" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={rescheduleDate}
                      onSelect={(d) => { setRescheduleDate(d); setRescheduleTime(''); setRescheduleDateOpen(false); }}
                      disabled={(date) => {
                        const startToday = new Date();
                        startToday.setHours(0, 0, 0, 0);
                        return date < startToday;
                      }}
                      initialFocus
                      className="bg-slate-800 text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">New Time</Label>
                <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                  <SelectTrigger className="h-11 bg-slate-700 border-slate-600 text-white" data-testid="booking-reschedule-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600 max-h-64">
                    {(() => {
                      const slots = TIME_SLOTS.filter((time) => {
                        if (!rescheduleDate) return true;
                        const now = new Date();
                        if (rescheduleDate.toDateString() !== now.toDateString()) return true;
                        return parseSlotToDate(time, rescheduleDate) > now;
                      });
                      if (slots.length === 0) {
                        return <div className="px-3 py-2 text-sm text-slate-400">No more slots today — pick a future date</div>;
                      }
                      return slots.map((time) => (
                        <SelectItem key={time} value={time} className="text-white hover:bg-slate-600">{time}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowReschedule(false)}
                  className="flex-1 bg-transparent border-slate-600 text-white hover:bg-slate-700"
                  data-testid="booking-reschedule-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReschedule}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="booking-reschedule-save-btn"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
